import { useRef, type ComponentType, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export type SegmentedOption<T extends string> = {
  value: T;
  label?: ReactNode;
  icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
  /** Required when the option shows only an icon. */
  ariaLabel?: string;
};

type SegmentedProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  "aria-label": string;
  /**
   * The `id` of the region whose content the switch replaces, when the page has
   * one. Every option points at it with `aria-controls`, so a screen reader can
   * jump from the switch to what it changed.
   */
  controls?: string;
  className?: string;
};

/** Where the arrow keys move from `index`, wrapping around the ends. */
function nextIndex(key: string, index: number, length: number): number | null {
  if (key === "ArrowRight" || key === "ArrowDown") return (index + 1) % length;
  if (key === "ArrowLeft" || key === "ArrowUp") return (index - 1 + length) % length;
  if (key === "Home") return 0;
  if (key === "End") return length - 1;
  return null;
}

/**
 * A view switch (list / calendar, month / week / day, all / WhatsApp). One of
 * the segments is always chosen, so it is a radio group: arrow keys move
 * between the options and choose as they go, and only the chosen one is in the
 * tab order (WAI-ARIA radiogroup, roving tabindex).
 *
 * The selected segment sits on `brand-soft` like an active nav item; the petrol
 * emphasis stays reserved for the page's primary action.
 */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  className,
  controls,
  "aria-label": ariaLabel,
}: SegmentedProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const target = nextIndex(event.key, index, options.length);
    if (target === null) return;
    event.preventDefault();
    onChange(options[target].value);
    groupRef.current
      ?.querySelectorAll<HTMLButtonElement>("[data-segmented-option]")
      ?.[target]?.focus();
  }

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-lg border border-app-border bg-app-surface p-0.5",
        className
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        const Icon = option.icon;
        // Nothing selected (a value the page does not offer) would leave the
        // group out of the tab order entirely; the first option carries it.
        const tabbable = selectedIndex === -1 ? index === 0 : selected;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            data-segmented-option=""
            aria-checked={selected}
            aria-label={option.ariaLabel}
            aria-controls={controls}
            title={option.ariaLabel}
            tabIndex={tabbable ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              // `min-w-0 whitespace-nowrap`: a consumer may stretch the group
              // to the full width of its card (the autonomy page does), and a
              // segment label must never become two lines in any of them.
              // `min-w-0` replaces the old `min-w-8`, which tailwind-merge
              // would drop anyway; an icon-only option still sizes to its
              // padding plus the 14px icon.
              "inline-flex h-8 min-w-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1",
              selected
                ? "bg-brand-soft text-brand"
                : "text-app-fg-muted hover:bg-app-surface-muted hover:text-app-fg"
            )}
          >
            {Icon ? <Icon className="size-3.5" aria-hidden="true" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
